import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Popper,
  ClickAwayListener,
} from "@mui/material";
import {
  Category,
  FilterPayload,
  Message,
} from "../../utility/interfaces/educationLibraryInterfaces";
import {
  areFiltersEqual,
  mapDefaultCheckboxesFromFilters,
  mapDefaultSwitchesFromFilters,
  mapUIStateToFilters,
} from "./FilterFunctions";
import { useGetEducationFiltersQuery } from "../learning-library/services/GetLearningFiltersAPISlice";
import { useAppSelector } from "../../store/hooks";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import {
  DEFAULT_LEARNING_FILTERS,
  FilterApiData,
  PRIMARY_GREEN,
} from "../../utility/constants/common-constants";
import CustomToggleSwitch from "../../components/v2/custom-switch/CustomToggleSwitch";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import { useTranslation } from "react-i18next";
import EmptyComponent from "../../components/v2/custom-empty-error-component/EmptyComponent";
import styles from "./LearningFilter.module.scss";

type LearningFilterProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (data: FilterPayload) => void;
  defaultFilters: FilterPayload;
};

const LearningFilter: React.FC<LearningFilterProps> = ({
  open,
  anchorEl,
  onClose,
  onApply,
  defaultFilters,
}) => {
  
  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const {
    data: filtersDataRaw,
    isLoading: filterDataLoading,
    isError: filterDataError,
  } = useGetEducationFiltersQuery({ patientPreferredLang });

  const { t } = useTranslation();
  const filtersData: Category[] = filtersDataRaw?.learningFilters || [];
  const general = filtersData.find((cat) => cat.categoryCode === FilterApiData.FILTER_CATEGORY_GENERAL);
  const restCategories = filtersData.filter(
    (cat) => cat.categoryCode !== FilterApiData.FILTER_CATEGORY_GENERAL
  );

  const [appliedFilters, setAppliedFilters] =
    useState<FilterPayload>(defaultFilters);

  const [switches, setSwitches] = useState(
    mapDefaultSwitchesFromFilters(general?.messages || [], defaultFilters)
  );

  const [checkboxes, setCheckboxes] = useState(
    mapDefaultCheckboxesFromFilters(restCategories, defaultFilters)
  );

  useEffect(() => {
    if (filtersData.length) {
      setSwitches(
        mapDefaultSwitchesFromFilters(general?.messages || [], defaultFilters)
      );
      setCheckboxes(
        mapDefaultCheckboxesFromFilters(restCategories, defaultFilters)
      );
    }
  }, [filtersDataRaw?.learningFilters?.length]);

  const isChecked = (categoryCode: string, item: Message) => {
    return checkboxes[categoryCode]?.includes(item.defaultContent) || false;
  };

  const handleSwitchChange =
    (label: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setSwitches((prev) => ({ ...prev, [label]: e.target.checked }));
    };

  const handleCheckboxChange = (categoryCode: string, item: Message) => {
    const label = item.defaultContent;
    const isSelectAll = label === FilterApiData.SELECT_ALL;

    const allOptions =
      restCategories
        .find((cat) => cat.categoryCode === categoryCode)
        ?.messages.filter((msg) => msg.defaultContent !== FilterApiData.SELECT_ALL)
        .map((msg) => msg.defaultContent) || [];

    setCheckboxes((prev) => {
      const selected = prev[categoryCode] || [];

      if (isSelectAll) {
        return { ...prev, [categoryCode]: [FilterApiData.SELECT_ALL] };
      } else {
        let updated = selected.includes(label)
          ? selected.filter((l) => l !== label)
          : [...selected.filter((l) => l !== FilterApiData.SELECT_ALL), label];

        const allSelectedNow = allOptions.every((option) =>
          updated.includes(option)
        );
        if (allSelectedNow) {
          updated = [FilterApiData.SELECT_ALL];
        } else if (updated.length === 0) {
          updated = [FilterApiData.SELECT_ALL];
        }

        return { ...prev, [categoryCode]: updated };
      }
    });
  };

  const handleApply = () => {
    const DefaultFilters = mapUIStateToFilters(
      switches,
      checkboxes,
      restCategories,
      defaultFilters
    );
    setAppliedFilters(DefaultFilters);
    onApply(DefaultFilters);
    onClose();
  };

  const handleReset = () => {
    const mergedDefaults: FilterPayload = {
      ...DEFAULT_LEARNING_FILTERS,
      category: defaultFilters.category,
    };
    const resetSwitches = mapDefaultSwitchesFromFilters(
      general?.messages || [],
      mergedDefaults
    );
    const resetCheckboxes = mapDefaultCheckboxesFromFilters(
      restCategories,
      mergedDefaults
    );

    setSwitches(resetSwitches);
    setCheckboxes(resetCheckboxes);

    const mapped = mapUIStateToFilters(
      resetSwitches,
      resetCheckboxes,
      restCategories,
      mergedDefaults
    );
    setAppliedFilters(mapped);
    onApply(mapped);
    onClose();
  };

  const isApplyDisabled = areFiltersEqual(
    mapUIStateToFilters(switches, checkboxes, restCategories, defaultFilters),
    appliedFilters
  );

  const handleClosePopup = () => {
    setSwitches(
      mapDefaultSwitchesFromFilters(general?.messages || [], appliedFilters)
    );
    setCheckboxes(
      mapDefaultCheckboxesFromFilters(restCategories, appliedFilters)
    );
    onClose();
  };

  return (
    <DataLoader
      isLoading={filterDataLoading}
      error={filterDataError}
      data={filtersData}
      loadingComponent={<Typography>Loading filters...</Typography>}
      errorComponent={
        <EmptyComponent
          message={t("educationContent.filterError")}
          textColor="error"
        />
      }
      emptyComponent={
        <EmptyComponent message={t("educationContent.filterNoData")} />
      }
    >
      {() => (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-end"
          disablePortal
          style={{ zIndex: 1300 }}
          modifiers={[{ name: "offset", options: { offset: [0, 10] } }]}
        >
          <ClickAwayListener onClickAway={handleClosePopup}>
            <Box
              className={styles.filterContainer}
            >
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                paddingX={1.5}
                paddingTop={2}
              >
                {general?.messages.map((msg) => (
                  <CustomToggleSwitch
                    key={msg.messageContextId}
                    label={msg.content}
                    checked={switches[msg.defaultContent]}
                    onChange={handleSwitchChange(msg.defaultContent)}
                  />
                ))}
              </Box>

              {restCategories.map((category) => (
                <Box key={category.categoryCode} mt={3}>
                  <Typography
                    fontWeight="bold"
                    textTransform="uppercase"
                    fontSize="14px"
                    gutterBottom
                  >
                    {category.category}
                  </Typography>
                  <FormGroup>
                    {category.messages.map((item) => (
                      <FormControlLabel
                        key={item.messageContextId}
                        control={
                          <Checkbox
                            checked={isChecked(category.categoryCode, item)}
                            onChange={() =>
                              handleCheckboxChange(category.categoryCode, item)
                            }
                            sx={{
                              padding: "6px",
                              color: PRIMARY_GREEN,
                            }}
                          />
                        }
                        label={item.content}
                      />
                    ))}
                  </FormGroup>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" mt={3}>
                <CustomButton
                className={styles.applyButton}
                  variant="contained"
                  text={t("educationContent.apply")}
                  onClick={handleApply}
                  disabled={isApplyDisabled}
                />

                <CustomButton
                  variant="text"
                  text={t("educationContent.reset")}
                  className={styles.resetButton}
                  onClick={handleReset}
                />
              </Box>
            </Box>
          </ClickAwayListener>
        </Popper>
      )}
    </DataLoader>
  );
};

export default LearningFilter;

